import * as THREE from 'three';
import {
    CabinetMesh,
    RectangularDimension,
    LShapedDimension,
} from 'components/customer/RoomPlanner/types';
import {store} from 'store/customer';
import {
    CabinetTransform,
    updateCabinetPosition,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';
import Camera from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {repositionCabinetDirection} from 'components/customer/RoomPlanner/helpers/repositionCabinet';
import {resetCabinetPosition} from 'components/customer/RoomPlanner/helpers/resetCabinetPosition';
import {DEFAULT_POSITION_Y} from 'components/customer/RoomPlanner/helpers/renderCabinetGroup';
import {calculatePositionY} from 'components/customer/RoomPlanner/helpers/calculatePositionY';
import {
    getDirection,
    getGroupWidth,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {updateCabinetsCurrentWall} from 'components/customer/RoomPlanner/helpers/boundaries/walls';

export const cabinetReposition = (
    wallRefs?: React.MutableRefObject<THREE.Mesh[]>,
    cabinetGroupList?: CabinetMesh[],
    prevRect?: RectangularDimension,
    prevLShape?: LShapedDimension,
    lengthChanged?: boolean,
    widthChanged?: boolean,
    leftWidthChanged?: boolean,
    rightWidthChanged?: boolean,
    rectChanged?: boolean,
    lShapedChanged?: boolean,
    oldPositionsRef?: React.MutableRefObject<
        Map<string | number, {x: number; z: number}>
    >,
    oldCabinets?: CabinetTransform[],
    heightChanged?: boolean,
    currentRect?: RectangularDimension,
    currentLShape?: LShapedDimension
) => {
    if (!wallRefs || !wallRefs.current || wallRefs.current.length === 0) {
        return;
    }

    // Initialize wall boundary variables
    let backWall: number | null = null;
    let leftWall: number | null = null;
    let leftWidthWall: number | null = null;
    let rightWidthWall: number | null = null;
    let rightDepthWall: number | null = null;
    let leftDepthWall: number | null = null;

    wallRefs.current.forEach((wall) => {
        // Calculate the bounding box for each wall
        const boundingBox = new THREE.Box3().setFromObject(wall);
        // Get wall identification
        const wallId =
            (typeof wall.userData.id === 'string' ? wall.userData.id : null) ||
            wall.name;

        // Check if bounding box has valid values (not NaN)
        const hasValidBounds =
            !isNaN(boundingBox.min.x) &&
            !isNaN(boundingBox.min.y) &&
            !isNaN(boundingBox.min.z) &&
            !isNaN(boundingBox.max.x) &&
            !isNaN(boundingBox.max.y) &&
            !isNaN(boundingBox.max.z);

        if (hasValidBounds) {
            // Save wall boundaries based on wall ID
            if (wallId === 'backWall' || wall.name === 'wall:back') {
                backWall = boundingBox.max.z;
            } else if (wallId === 'leftWall' || wall.name === 'wall:left') {
                leftWall = boundingBox.min.x;
            } else if (
                wallId === 'leftWidthWall' ||
                wall.name === 'leftWidthWall'
            ) {
                leftWidthWall = boundingBox.max.z;
            } else if (
                wallId === 'rightDepthWall' ||
                wall.name === 'rightDepthWall'
            ) {
                rightDepthWall = boundingBox.max.z;
            } else if (
                wallId === 'leftDepthWall' ||
                wall.name === 'leftDepthWall'
            ) {
                leftDepthWall = boundingBox.max.x;
            } else if (
                wallId === 'rightWidthWall' ||
                wall.name === 'rightWidthWall'
            ) {
                rightWidthWall = boundingBox.max.x;
            }
        }
    });

    if (cabinetGroupList && cabinetGroupList.length > 0) {
        // First update all cabinets' current wall assignments
        updateCabinetsCurrentWall(cabinetGroupList, Camera.scene);

        const prevWallLengthNormalized: number =
            (prevRect.length * 0.2) / 2 + 12.5;
        const wallDifferenceLength: number =
            Math.abs(backWall) - prevWallLengthNormalized;

        const prevWallWidthNormalized: number = (prevRect.width * 0.2) / 2 - 10;
        const wallDifferenceWidth: number =
            Math.abs(leftWall) - prevWallWidthNormalized;

        const prevWallLengthLShapeNormalized: number =
            (prevLShape.leftWidth * 0.2) / 2 - 12.5;
        const wallDifferenceLengthLShape: number =
            Math.abs(leftWidthWall) - prevWallLengthLShapeNormalized;

        const prevWallWidthLShapeNormalized: number =
            (prevLShape.rightWidth * 0.2) / 2 + 60;
        const wallDifferenceWidthLShape: number =
            Math.abs(rightWidthWall) - prevWallWidthLShapeNormalized;

        // Calculate current wall height
        const scale = 0.2;
        const currentWallHeight =
            currentRect && currentLShape
                ? (store.getState().roomPlanner.roomType === 'RECTANGULAR'
                      ? currentRect.height
                      : currentLShape.height) * scale
                : getRoomDimensionsComputed().wallHeight;

        // Reposition base products to their current wall's new bounding box (same as upper products)
        cabinetGroupList.forEach((product) => {
            const name = product.userData.id;

            if (!oldPositionsRef.current.has(name)) {
                oldPositionsRef.current.set(name, {
                    x: product.position.x,
                    z: product.position.z,
                });
            }

            const oldPos = oldPositionsRef.current.get(name);
            if (!oldPos) {
                return;
            }

            if (rectChanged) {
                if (widthChanged) {
                    if (
                        Math.floor(prevWallWidthNormalized) !==
                        Math.floor(Math.abs(leftWall))
                    ) {
                        repositionCabinetDirection({
                            product,
                            wallDifferenceWidth,
                            oldPositionX: {current: oldPos.x},
                        });
                    } else if (
                        Math.floor(prevWallWidthNormalized) ===
                        Math.floor(Math.abs(leftWall))
                    ) {
                        resetCabinetPosition(product, oldCabinets, 'x');
                    }
                }
                if (lengthChanged) {
                    if (
                        Math.floor(prevWallLengthNormalized) !==
                        Math.floor(Math.abs(backWall))
                    ) {
                        repositionCabinetDirection({
                            product,
                            wallDifferenceLength,
                            oldPositionZ: {current: oldPos.z},
                        });
                    } else if (
                        Math.floor(prevWallLengthNormalized) ===
                        Math.floor(Math.abs(backWall))
                    ) {
                        resetCabinetPosition(product, oldCabinets, 'z');
                    }
                }
            }

            if (lShapedChanged) {
                if (leftWidthChanged) {
                    if (
                        Math.floor(prevWallLengthLShapeNormalized) !==
                        Math.floor(Math.abs(leftWidthWall))
                    ) {
                        repositionCabinetDirection({
                            product,
                            wallDifferenceLength: wallDifferenceLengthLShape,
                            oldPositionZ: {current: oldPos.z},
                        });
                    } else if (
                        Math.floor(prevWallLengthLShapeNormalized) ===
                        Math.floor(Math.abs(leftWidthWall))
                    ) {
                        resetCabinetPosition(product, oldCabinets, 'z');
                    }
                }

                if (rightWidthChanged) {
                    if (
                        Math.floor(prevWallWidthLShapeNormalized) !==
                        Math.floor(Math.abs(rightWidthWall))
                    ) {
                        repositionCabinetDirection({
                            product,
                            wallDifferenceWidth: wallDifferenceWidthLShape,
                            oldPositionX: {current: oldPos.x},
                        });
                    } else if (
                        Math.floor(prevWallWidthLShapeNormalized) ===
                        Math.floor(Math.abs(rightWidthWall))
                    ) {
                        resetCabinetPosition(product, oldCabinets, 'x');
                    }
                }
            }

            // Handle wall height changes
            if (heightChanged && (rectChanged || lShapedChanged)) {
                const positionY = calculatePositionY(
                    product,
                    currentWallHeight,
                    DEFAULT_POSITION_Y
                );

                product.position.y = positionY;
                product.userData.wallHeight = currentWallHeight;

                resetCabinetPosition(product, oldCabinets, 'y');
            }

            if (!oldPositionsRef.current.has(name)) {
                oldPositionsRef.current.set(name, {
                    x: product.position.x,
                    z: product.position.z,
                });
            }
        });

        leftWall = prevWallWidthNormalized;
        backWall = prevWallLengthNormalized;
        leftWidthWall = prevWallLengthLShapeNormalized;
        rightWidthWall = prevWallWidthLShapeNormalized;
    }

    //
    repositionCabinetsOutsideRoom(
        cabinetGroupList,
        rightDepthWall,
        leftDepthWall,
        oldPositionsRef
    );
};

/**
 * Checks if a cabinet is outside room boundaries and repositions cabinets in rows if needed.
 * @param {CabinetMesh[]} cabinets - Array of cabinet meshes to check.
 * @param {number} [rightDepthWall] - Optional depth of the right wall.
 * @param {number} [leftDepthWall] - Optional depth of the left wall.
 * @param {Object} [oldPositionsRef] - Optional ref storing previous cabinet positions.
 * @param {Map<string|number, {x: number, z: number}>} [oldPositionsRef.current] - The map of previous positions.
 */
export const repositionCabinetsOutsideRoom = (
    cabinets: CabinetMesh[],
    rightDepthWall?: number,
    leftDepthWall?: number,
    oldPositionsRef?: React.MutableRefObject<
        Map<string | number, {x: number; z: number}>
    >
) => {
    // Get room dimensions
    const dimensions = getRoomDimensionsComputed();
    const wallMargin = 36;

    // Calculate room boundaries - use proper back wall position
    const backWallZ = dimensions.roomDepthComputed + 50; // Approximate back wall position

    const roomBounds = {
        minX: -dimensions.roomWidth / 2 + wallMargin,
        maxX: dimensions.roomWidth / 2 - wallMargin,
        minZ: backWallZ - 50, // Back wall area
        maxZ: backWallZ + 400, // Front area (generous range)
    };

    // Find cabinets that are outside room boundaries
    const cabinetsOutsideRoom: CabinetMesh[] = [];

    cabinets.forEach((cabinet) => {
        const cabinetBox = new THREE.Box3().setFromObject(cabinet);
        // More tolerant boundary check - only consider cabinets significantly outside room boundaries
        // This prevents cabinets close to walls from being unnecessarily repositioned
        const tolerance = 100; // Additional tolerance for cabinets near walls

        if (dimensions.roomType !== 'RECTANGULAR') {
            if (
                cabinetBox.min.z > rightDepthWall &&
                cabinetBox.max.x > leftDepthWall
            ) {
                cabinetsOutsideRoom.push(cabinet);
            }
        }

        const isOutside =
            cabinetBox.min.x < roomBounds.minX - tolerance ||
            cabinetBox.max.x > roomBounds.maxX + tolerance ||
            cabinetBox.min.z < roomBounds.minZ - tolerance ||
            cabinetBox.max.z > Math.abs(roomBounds.minZ) + tolerance;

        if (isOutside) {
            cabinetsOutsideRoom.push(cabinet);
        }
    });

    // If no cabinets are outside, return early
    if (cabinetsOutsideRoom.length === 0) {
        return;
    }

    // Reposition cabinets outside room using row-based logic
    repositionCabinetsInRows(
        cabinetsOutsideRoom,
        cabinets,
        roomBounds,
        oldPositionsRef
    );
};

/**
 * Repositions cabinets in rows starting from the left corner of the back wall.
 * Similar to the adding cabinet logic in renderCabinetGroup.ts
 *
 * @param {CabinetMesh[]} cabinetsToReposition - Cabinets that need repositioning
 * @param {CabinetMesh[]} allCabinets - All cabinets for overlap checking
 * @param {Object} roomBounds - Room boundary coordinates
 * @param {number} roomBounds.minX - Minimum X coordinate of the room
 * @param {number} roomBounds.maxX - Maximum X coordinate of the room
 * @param {number} roomBounds.minZ - Minimum Z coordinate of the room
 * @param {number} roomBounds.maxZ - Maximum Z coordinate of the room
 * @param {React.MutableRefObject<Map<string|number, {x: number, z: number}>>} [oldPositionsRef] - Optional ref storing previous cabinet positions
 */
const repositionCabinetsInRows = (
    cabinetsToReposition: CabinetMesh[],
    allCabinets: CabinetMesh[],
    roomBounds: {minX: number; maxX: number; minZ: number; maxZ: number},
    oldPositionsRef?: React.MutableRefObject<
        Map<string | number, {x: number; z: number}>
    >
) => {
    // Configurable Z offset for new rows (same as in renderCabinetGroup.ts)
    const ROW_Z_OFFSET = 300;

    // Find existing cabinets in the back wall to determine starting position
    const backWallCabinets = allCabinets.filter((cabinet) => {
        // Check if cabinet is in the back wall area (normal rotation, facing front)
        const direction = getDirection(cabinet.rotation.z);
        return (
            direction === 'normal' &&
            cabinet.position.z >= roomBounds.minZ &&
            cabinet.position.z <= roomBounds.minZ
        );
    });

    // Find the rightmost position of existing back wall cabinets
    let currentRowStartX = roomBounds.minX + 0.5;
    let currentRowZ = roomBounds.minZ; // Start at back wall

    if (backWallCabinets.length > 0) {
        // Find the rightmost cabinet in the back wall
        const rightmostCabinet = backWallCabinets.reduce(
            (rightmost, cabinet) => {
                const cabinetBox = new THREE.Box3().setFromObject(cabinet);
                const rightmostBox = new THREE.Box3().setFromObject(rightmost);
                return cabinetBox.max.x > rightmostBox.max.x
                    ? cabinet
                    : rightmost;
            }
        );

        const rightmostBox = new THREE.Box3().setFromObject(rightmostCabinet);
        currentRowStartX = rightmostBox.max.x + 5; // Start after the rightmost cabinet with gap

        // Calculate the base Z position (without depth offset) from existing cabinet
        const rightmostCabinetDepth = getGroupWidth(rightmostCabinet).depth;
        currentRowZ = rightmostCabinet.position.z - rightmostCabinetDepth; // Remove depth to get base wall position
    }

    cabinetsToReposition.forEach((cabinet) => {
        // Reset cabinet rotation to default (normal direction, facing front)
        cabinet.rotation.z = 0;
        cabinet.userData.currentDirection = 'normal';

        // Get cabinet dimensions to calculate proper wall offset
        const {depth} = getGroupWidth(cabinet);

        // Position cabinet flush against the back wall (same as cabinetReposition logic)
        // Add depth to push cabinet away from wall surface so it sits flush against it
        const flushZ = currentRowZ + depth;

        // Position cabinet at current row position
        cabinet.position.set(currentRowStartX, cabinet.position.y, flushZ);
        cabinet.updateMatrixWorld();

        // Check for overlaps and move right until no overlaps remain
        let hasOverlap = true;
        const maxIterations = 20; // Safety limit
        let iterations = 0;

        while (hasOverlap && iterations < maxIterations) {
            hasOverlap = false;
            iterations++;

            const currentCabinetBox = new THREE.Box3().setFromObject(cabinet);

            // Check if cabinet exceeds room's right boundary
            if (currentCabinetBox.max.x > roomBounds.maxX) {
                // Move to next row - go back to left corner with Z offset
                currentRowZ += ROW_Z_OFFSET;
                currentRowStartX = roomBounds.minX + 0.5;

                // Recalculate flush Z position for new row
                const newFlushZ = currentRowZ + depth;
                cabinet.position.set(
                    currentRowStartX,
                    cabinet.position.y,
                    newFlushZ
                );
                cabinet.updateMatrixWorld();
                hasOverlap = true;
                continue;
            }

            // Check for overlaps with other cabinets
            for (const otherCabinet of allCabinets) {
                if (otherCabinet.userData.id === cabinet.userData.id) continue;

                const otherBox = new THREE.Box3().setFromObject(otherCabinet);
                const currentBox = new THREE.Box3().setFromObject(cabinet);
                if (currentCabinetBox.intersectsBox(otherBox)) {
                    // Move cabinet to the right
                    const otherMaxX = otherBox.max.x;
                    const currentMinX = currentBox.min.x;
                    cabinet.position.x += otherMaxX - currentMinX + 0.01;
                    cabinet.updateMatrixWorld();
                    hasOverlap = true;
                    break;
                }
            }

            const name = cabinet.userData.id;

            if (oldPositionsRef?.current.has(name)) {
                oldPositionsRef.current.set(name, {
                    x: cabinet.position.x,
                    z: cabinet.position.z,
                });
            }
        }

        const id = Number(cabinet.userData?.values?.id);

        let index = 0;
        if (cabinet?.userData?.id != 'undefined') {
            [, index] = String(cabinet.userData.id)
                .split('-')
                .map((val) => Number(val));
        }

        store.dispatch(
            updateCabinetPosition({
                rotation: cabinet.rotation,
                position: cabinet.position,
                id,
                index,
            })
        );

        // Update current row start position for next cabinet
        const finalCabinetBox = new THREE.Box3().setFromObject(cabinet);
        currentRowStartX = finalCabinetBox.max.x; // Small gap for next cabinet

        // If we've reached the end of the row, reset for next row
        if (currentRowStartX > roomBounds.maxX) {
            currentRowStartX = roomBounds.minX + 0.5;
            currentRowZ += ROW_Z_OFFSET;
        }
    });
};
